import { useNavigate, useRouterState } from '@tanstack/react-router'
import { LogOut, Settings2, UserCog } from 'lucide-react'
import { Avatar } from '@/components/ui/display'
import { CaptureMenu } from '@/features/meetings/CaptureMenu'
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui/overlays'
import { signOut } from '@/app/auth'
import { currentPerson } from '@/data/people'
import { cn } from '@/lib/utils'

/**
 * Three controls, because there are three questions.
 *
 * The plus RECORDS something — a meeting happening now, or one that already
 * happened somewhere else. It is the only control here that adds rather than
 * adjusts, which is why it leads.
 *
 * The gear is the ORGANISATION — what the product does to every meeting in the
 * tenant, decided by an admin for everyone. The avatar is the PERSON — this
 * account, and how it sees things. They were one menu, which made a tenant-wide
 * recording policy something you reached by way of your own profile.
 *
 * No search field. The palette is ⌘K from anywhere, and a permanent 16rem box
 * whose only job was to proxy a keystroke was the widest thing in a bar that
 * otherwise holds two icons.
 */
export function TopBar() {
  const navigate = useNavigate()
  const inSettings = useRouterState({
    select: (state) => state.location.pathname.startsWith('/settings'),
  })

  return (
    <header className="flex h-12 shrink-0 items-center justify-end gap-1 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-3">
      {/* First, because it ADDS a meeting: the other two controls are about
          what already exists. */}
      <CaptureMenu />

      <button
        type="button"
        aria-label="Organisation settings"
        aria-current={inSettings ? 'page' : undefined}
        onClick={() => void navigate({ to: '/settings' })}
        className={cn(
          'grid size-8 place-items-center rounded-[var(--radius-sm)] outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35',
          inSettings
            ? 'bg-[var(--color-surface-active)] text-[var(--color-fg)]'
            : 'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]'
        )}
      >
        <Settings2 className="size-4" />
      </button>

      <Menu>
        <MenuTrigger className="ml-0.5 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35">
          <Avatar name={currentPerson().name} src={currentPerson().imageUrl} size="md" />
        </MenuTrigger>
        <MenuContent className="w-60">
          <div className="px-2 py-1.5">
            <p className="truncate text-xs font-medium text-[var(--color-fg)]">
              {currentPerson().name}
            </p>
            <p className="truncate text-2xs text-[var(--color-fg-subtle)]">{currentPerson().email}</p>
          </div>

          <MenuSeparator />

          {/* Your own settings — recording and calendars — as opposed to the
              organisation's, which is the gear. */}
          <MenuItem onClick={() => void navigate({ to: '/account' })}>
            <UserCog className="size-3.5" />
            Your account
          </MenuItem>

          <MenuSeparator />

          {/* Which tenant this account is in. Context, not a control — there is
              no second organisation to switch to. */}
          <div className="px-2 py-1.5">
            <p className="text-2xs text-[var(--color-fg-subtle)]">Organisation</p>
            <p className="truncate text-xs text-[var(--color-fg-muted)]">nohup</p>
          </div>

          <MenuSeparator />

          <MenuItem onClick={() => void signOut()}>
            <LogOut className="size-3.5" />
            Sign out
          </MenuItem>
        </MenuContent>
      </Menu>
    </header>
  )
}
